import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
    showImage = "showImage",
    showSpecialMessage = "showSpecialMessage",
    showNewsSummary = "showNewsSummary",
    showAuthor = "showAuthor",
    showPublishDate = "showPublishDate",
    showReadMoreButton = "showReadMoreButton",
    showViewAllButton = "showViewAllButton",
    showTitle = "showTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.showImage]: boolean;
        [fields.showSpecialMessage]: boolean;
        [fields.showNewsSummary]: boolean;
        [fields.showAuthor]: boolean;
        [fields.showPublishDate]: boolean;
        [fields.showReadMoreButton]: boolean;
        [fields.showViewAllButton]: boolean;
        [fields.showTitle]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: getCurrentPage(state).nodeId,
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[getCurrentPage(state).nodeId] ?? {
            parentNodeId: getCurrentPage(state).nodeId,
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: GridContainerProps;
    newsWrapper?: GridItemProps;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    viewallnews?: LinkPresentationProps;
    buttonWrapper?: InjectableCss;
    test?: InjectableCss;
    ContentImg?: InjectableCss;
    specialAlert?: InjectableCss;
    ImageAndSpecialwrapper?: InjectableCss;
    main?: InjectableCss;
    showOnlyReadMoreWrapper?: InjectableCss;
    showOnlyReadMore?: LinkPresentationProps;
    paginationWrapper?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    titleText: {
        variant: "h4",
        css: css`
            margin-top: 5px;
            margin-bottom: 0;
            opacity: 1;
            color: ${getColor("secondary")} !important;
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            /* font-size: ${TYPOGRAPHY.size.large}; */
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.small};
            text-align: left;
            text-transform: uppercase;
            line-height: ${TYPOGRAPHY.lineHeight.smallbase};
            > a {
                > span {
                    opacity: 1;
                    color: ${getColor("secondary")} !important;
                    font-family: ${TYPOGRAPHY.fontFamily.dosis};
                    /* font-size: ${TYPOGRAPHY.size.large}; */
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    font-style: normal;
                    letter-spacing: ${TYPOGRAPHY.letterSpacing.small};
                    text-align: left;
                    text-transform: uppercase;
                    line-height: ${TYPOGRAPHY.lineHeight.smallbase};
                    &:hover {
                        color: ${getColor("primary")} !important;
                    }
                }
            }
            &:hover {
                color: ${getColor("primary")} !important;
            }
        `,
    },
    newsWrapper: {
        width: [12, 12, 6, 4, 4],
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 15px;
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
                nav {
                    ul {
                        li {
                            button[data-test-selector^="currentPageNumber_"]:hover {
                                background: #ffc808;
                            }
                        }
                    }
                }
            `,
        },
    },

    paginationWrapper: {
        css: css``,
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
            width: 100%;
            margin-bottom: 8px;
            > p {
                font-size: ${TYPOGRAPHY.size.small};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
            }
        `,
    },
    authorText: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            opacity: 1;
            color: ${getColor("secondary")};
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.small};
            text-align: left;
            text-transform: uppercase;
            line-height: ${TYPOGRAPHY.lineHeight.mSmallestbase};
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;
            max-width: 100%;
            margin-top: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        font-size: ${TYPOGRAPHY.size.large};
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.base};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
            margin-top: 8px;
            opacity: 0.8;
            color: ${getColor("secondary")};
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            text-align: left;
            line-height: ${TYPOGRAPHY.lineHeight.vSmallestbase};
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 5px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    listWrapper: {
        gap: 20,
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    null,
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            gap: 4px;
            margin-top: 5px;
        `,
    },
    readMoreLink: {
        css: css`
            width: auto;
            border-radius: 25px 0 0 25px;
            background-color: ${getColor("warning")};
            padding: 4px 10px;
            &:hover {
                background: ${getColor("warning.contrast")};
            }
            span {
                color: ${getColor("info.contrast")} !important;
                font-size: ${TYPOGRAPHY.size.smallest} !important;
                opacity: 1;
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.vSmaller};
                text-align: left;
            }
        `,
    },
    viewallnews: {
        css: css`
            display: flex;
            width: auto;
            border: 1px solid ${COLORS.normalGray};
            border-radius: 0 25px 25px 0;
            background: ${getColor("danger.contrast")};
            font-size: ${TYPOGRAPHY.size.smallest};
            padding: 4px 6px;
            &:hover {
                &::after {
                    background: ${COLORS.yellow};
                    border: 0.25px solid rgba(17, 41, 69, 0.22);
                    box-shadow: inset 0 0 2px 0 rgba(0, 0, 0, 0.64);
                }
            }
            span {
                color: ${getColor("success.contrast")} !important;
                font-size: ${TYPOGRAPHY.size.smallest} !important;
                opacity: 1;
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.vSmaller};
                text-align: left;
            }

            &::after {
                content: "";
                width: 11px;
                margin-left: 13px !important;
                height: 11px;
                border-radius: 50%;
                display: inline-block;
                position: relative;
                margin: auto;
                left: -6px;
                border-radius: 5.499547px;
                border: 0.25px solid rgba(17, 41, 69, 0.219214);
                box-shadow: 0px 0px 2px rgb(0 0 0 / 64%) inset;
                opacity: 1;
                background-color: ${COLORS.background.whiteSmoke};
            }
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    ContentImg: {
        css: css`
            width: 100%;
            height: 100%;
            img {
                border-radius: 5px;
                object-fit: cover;
                aspect-ratio: 3/1.7 !important;
                height: auto;
                width: 100%;
                max-height: 175px !important;
            }
            &::after {
                content: "";
                position: absolute;
                width: 49px;
                bottom: -24px;
                right: -24px;
                height: 49px;
                border: 2px solid transparent;
                background-color: ${COLORS.white};
                border-image-slice: 10%;
                transform: rotateZ(45deg);
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: 191px !important;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    specialAlert: {
        css: css`
            position: absolute;
            bottom: 20px;
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            max-height: 35px;
            padding: 11px;
            border-radius: 0px 20.5px 20.5px 0px;
            opacity: 1;
            background-color: ${getColor("primary")};
            color: ${COLORS.white};
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            text-align: center;
            line-height: ${TYPOGRAPHY.lineHeight.vSmallestbase};
            text-transform: uppercase;
        `,
    },
    ImageAndSpecialwrapper: {
        css: css`
            position: relative;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,

                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    main: {
        css: css`
            padding-top: 20px;
            padding-bottom: 96px;
        `,
    },
    showOnlyReadMoreWrapper: {
        css: css`
            width: 100%;
        `,
    },
    showOnlyReadMore: {
        css: css`
            border-radius: 25px;
            background-color: ${getColor("warning")};
            padding: 4px 10px;
            &:hover {
                background: ${getColor("warning.contrast")};
            }
            span {
                font-size: ${TYPOGRAPHY.size.smallest} !important;
                opacity: 1;
                color: ${getColor("info.contrast")} !important;
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.vSmaller};
                text-align: left;
            }
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: {
                defaultPageSize,
                showImage,
                showSpecialMessage,
                showNewsSummary,
                showAuthor,
                showPublishDate,
                showReadMoreButton,
                showViewAllButton,
                showTitle,
            },
            parameter,
        } = this.props;

        if (!pages) {
            return null;
        }

        const totalItems = pages.length;
        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;

        const hideButton = {
            css: css`
                display: none;
            `,
        };

        const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"])
            .reverse()
            .slice((page - 1) * pageSize, page * pageSize);

        return (
            <StyledWrapper {...styles.main}>
                <GridContainer {...styles.listWrapper}>
                    {sortedAndPagedPages.map((page: PageProps) => (
                        <GridItem {...styles.newsWrapper} key={page.id}>
                            {showPublishDate && page.fields["publishDate"] && (
                                <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                                    <LocalizedDateTime
                                        dateTime={new Date(page.fields["publishDate"])}
                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                    />
                                </Typography>
                            )}
                            <StyledWrapper {...styles.ImageAndSpecialwrapper}>
                                {showImage && page.fields["img"] && (
                                    <LazyImage {...styles.ContentImg} src={page.fields["img"]} />
                                )}
                                {showImage &&
                                    showSpecialMessage &&
                                    page.fields["img"] &&
                                    page.fields["specialMessage"] && (
                                        <StyledWrapper {...styles.specialAlert}>
                                            {page.fields["specialMessage"]}
                                        </StyledWrapper>
                                    )}
                            </StyledWrapper>
                            {showTitle && (
                                <Typography variant="h4" {...styles.titleText}>
                                    <Link href={links[page.nodeId]?.url} data-test-selector="newslist_pagetitle">
                                        {page.fields["title"]}
                                    </Link>
                                </Typography>
                            )}
                            {showNewsSummary && (page.fields["newsSummary"] || page.fields["newsContent"]) && (
                                <StyledWrapper {...styles.summaryWrapper} data-test-selector="newslist_content">
                                    {parse(
                                        page.fields["newsSummary"] ||
                                            truncate(page.fields["newsContent"], { length: 180 }),
                                        parserOptions,
                                    )}
                                </StyledWrapper>
                            )}
                            {/* <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                {showAuthor && page.fields["author"] && (
                                    <Typography {...styles.authorText} data-test-selector="newslist_author">
                                        {page.fields["author"]}
                                    </Typography>
                                )}
                                {showPublishDate && page.fields["publishDate"] && (
                                    <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                                        <LocalizedDateTime
                                            dateTime={new Date(page.fields["publishDate"])}
                                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                        />
                                    </Typography>
                                )}
                            </StyledWrapper> */}
                            <StyledWrapper {...styles.buttonWrapper}>
                                {showReadMoreButton && (
                                    <Link
                                        href={links[page.nodeId]?.url}
                                        // {...styles.readMoreLink}
                                        {...(showReadMoreButton && showViewAllButton && page.fields["additionalLink"]
                                            ? styles.readMoreLink
                                            : styles.showOnlyReadMore)}
                                        data-test-selector={`newsList_ReadMore_${page.fields["title"]}`}
                                    >
                                        {translate("Read More")}
                                    </Link>
                                )}
                                <Link
                                    href={page.fields["additionalLink"]}
                                    // {...styles.viewallnews}
                                    target="_blank"
                                    {...(page.fields["additionalLink"] && showReadMoreButton && showViewAllButton
                                        ? styles.viewallnews
                                        : hideButton)}
                                    data-test-selector={`newsList_ReadMore_${page.fields["title"]}`}
                                >
                                    {page.fields["additionalLinkLabel"] || translate("View all News")}
                                </Link>
                            </StyledWrapper>
                        </GridItem>
                    ))}
                </GridContainer>
                <StyledWrapper {...styles.paginationWrapper} className="nl-pagination-wrapper">
                    {totalItems > defaultPageSize && (
                        <Pagination
                            resultsCount={totalItems}
                            currentPage={page}
                            resultsPerPage={pageSize}
                            resultsPerPageOptions={[
                                defaultPageSize,
                                defaultPageSize * 2,
                                defaultPageSize * 3,
                                defaultPageSize * 4,
                            ]}
                            onChangePage={this.changePage}
                            onChangeResultsPerPage={this.changeResultsPerPage}
                            pageSizeCookie="NewsList-PageSize"
                            {...styles.pagination}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
                sortOrder: 1,
            },
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showSpecialMessage,
                displayName: "Show SpecialMessage",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 3,
                isVisible: item => item.fields.showImage,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 4,
            },
            {
                name: fields.showNewsSummary,
                displayName: "Show Summary",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 5,
            },
            // {
            //     name: fields.showAuthor,
            //     displayName: "Show Author",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: true,
            //     fieldType: "General",
            //     sortOrder: 6,
            // },
            {
                name: fields.showPublishDate,
                displayName: "Show PublishDate",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 7,
            },
            {
                name: fields.showReadMoreButton,
                displayName: "Show ReadMore Button",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                sortOrder: 8,
            },
            {
                name: fields.showViewAllButton,
                displayName: "Show View All Button",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
                isVisible: item => item.fields.showReadMoreButton,
            },
        ],
    },
};

export default widgetModule;
